import { AppBar, Box, Button, Container, IconButton, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { HashRouter as Router, useLocation } from 'react-router-dom';
import { auth, googleProvider } from './firebase';
import { signInWithPopup, signOut } from 'firebase/auth';

import AppDrawer from './components/AppDrawer';
import AppRoutes from './components/AppRoutes';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from './hooks/useAuth';

const drawerWidth = 240;

const AppContent: React.FC = () => {
  const { user, loading, hasPermissions } = useAuth();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [drawerOpen, setDrawerOpen] = useState(isLargeScreen);
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(isLargeScreen);
  }, [isLargeScreen]);

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          ...(location.pathname === '/tv' && {
            display: { md: 'none' },
          })
        }}
      >
        <Toolbar>
          {user && hasPermissions && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Survey Polls
          </Typography>
          {user ? (
            <Button color="inherit" onClick={handleSignOut}>Sign Out</Button>
          ) : (
            <Button color="inherit" onClick={handleSignIn}>Sign In with Google</Button>
          )}
        </Toolbar>
      </AppBar>

      {user && hasPermissions && (
        <AppDrawer 
          drawerWidth={drawerWidth} 
          isLargeScreen={isLargeScreen} 
          drawerOpen={drawerOpen} 
          setDrawerOpen={setDrawerOpen} 
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(isLargeScreen && {
            marginLeft: drawerOpen ? 0 : `-${drawerWidth}px`,
          }),
        }}
      >
        <Toolbar 
          sx={{
            ...(location.pathname === '/tv' && {
              display: { md: 'none' },
            })
          }}
        />
        <Container maxWidth="lg" disableGutters>
          <AppRoutes />
        </Container>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
